import React, { useState } from "react";
import { Share2, Flame, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { FavoriteButton } from "@/components/ui/FavoriteButton";

interface PropertyBadge {
  type: 'new' | 'price-drop' | 'featured' | 'launch' | 'engagement' | 'partner';
  label: string;
  value?: string;
}

interface PropertyCardProps {
  id: string | number;
  title: string;
  code?: string;
  type?: string;
  attributes?: string;
  // Nova estrutura para suportar múltiplas finalidades
  prices?: {
    sale?: {
      value: string;
      label: string;
      condominium?: string;
      iptu?: string;
    };
    rental?: {
      value: string;
      unit: string;
      label: string;
      condominium?: string;
      iptu?: string;
    };
  };
  // Compatibilidade reversa (sempre preenchido)
  price: string;
  priceUnit?: string;
  additionalCosts?: string;
  saleType?: "Locação" | "Venda";
  address: string;
  image: string | string[];
  badges?: PropertyBadge[];
  isNew?: boolean;
  engagementCount?: number;
  onClick?: () => void;
  variant?: 'default' | 'compact' | 'featured' | 'grid';
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

function PropertyCard({
  id,
  title,
  code,
  type,
  attributes,
  prices,
  price,
  priceUnit,
  additionalCosts,
  address,
  image,
  saleType,
  badges = [],
  isNew = false,
  engagementCount,
  onClick,
  variant = 'default',
  className,
  onMouseEnter,
  onMouseLeave
}: PropertyCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const images = Array.isArray(image) ? image : [image];

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };

  const handleClick = () => {
    onClick?.();
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const propertyUrl = `${window.location.origin}/imovel/${code || id}`;
    const shareData = {
      title: title,
      text: `Confira este imóvel: ${title}`,
      url: propertyUrl
    };

    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(propertyUrl);
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 3000);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Erro ao compartilhar:', error);
        try {
          await navigator.clipboard.writeText(propertyUrl);
          setShowCopyToast(true);
          setTimeout(() => setShowCopyToast(false), 3000);
        } catch (clipboardError) {
          alert('Não foi possível compartilhar. URL: ' + propertyUrl);
        }
      }
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'compact':
        return {
          containerWidth: 250,
          isFlexible: false,
          paddingClass: "p-3",
          imageWidth: 220,
          imageHeight: 150,
          titleSize: "text-sm font-medium line-clamp-2",
          metaSize: "text-[9px]",
          attributesSize: "text-xs font-thin",
          priceSize: "text-xl",
          priceUnitSize: "text-xs",
          addCostsSize: "text-[10px]",
          addressSize: "text-[11px]",
          iconSize: "w-3.5 h-3.5",
          bodyGapClass: "gap-2",
        } as const;
      case 'featured':
        return {
          containerWidth: 310,
          isFlexible: false,
          paddingClass: "p-4",
          imageWidth: 270,
          imageHeight: 200,
          titleSize: "text-base font-medium",
          metaSize: "text-[10px]",
          attributesSize: "text-base",
          priceSize: "text-2xl",
          priceUnitSize: "text-sm",
          addCostsSize: "text-xs",
          addressSize: "text-xs",
          iconSize: "w-4 h-4",
          bodyGapClass: "gap-[11px]"
        } as const;
      case 'grid':
        return {
          containerWidth: 'auto',
          isFlexible: true,
          minWidth: 200,
          paddingClass: "p-3",
          imageWidth: '100%',
          imageHeight: 160,
          titleSize: "text-sm font-medium line-clamp-2",
          metaSize: "text-[9px]",
          attributesSize: "text-xs",
          priceSize: "text-lg",
          priceUnitSize: "text-xs",
          addCostsSize: "text-[10px]",
          addressSize: "text-[10px]",
          iconSize: "w-3.5 h-3.5",
          bodyGapClass: "gap-2",
        } as const;
      default:
        return {
          containerWidth: 310,
          isFlexible: false,
          paddingClass: "p-4",
          imageWidth: 270,
          imageHeight: 200,
          titleSize: "text-base font-medium",
          metaSize: "text-[10px]",
          attributesSize: "text-base",
          priceSize: "text-2xl",
          priceUnitSize: "text-sm",
          addCostsSize: "text-xs",
          addressSize: "text-xs",
          iconSize: "w-4 h-4",
          bodyGapClass: "gap-[11px]"
        } as const;
    }
  };

  const styles = getVariantStyles();

  return (
    <article
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        "flex flex-col gap-2 bg-white rounded-2xl shadow-[0px_2px_8px_0px_rgba(0,0,0,0.08),0px_1px_2px_0px_rgba(0,0,0,0.04)] transition-all duration-200 hover:shadow-[0px_4px_16px_0px_rgba(0,0,0,0.12),0px_2px_4px_0px_rgba(0,0,0,0.06)] hover:scale-[1.02] overflow-hidden",
        styles.paddingClass,
        styles.isFlexible && "w-full",
        onClick && "cursor-pointer",
        className
      )}
      style={styles.isFlexible ? {
        minWidth: `${styles.minWidth}px`,
        width: '100%'
      } : {
        minWidth: `${styles.containerWidth}px`,
        maxWidth: `${styles.containerWidth}px`,
        width: `${styles.containerWidth}px`
      }}
    >
      {/* Header - Tag e Botões */}
      <div className="flex justify-between items-center w-full">
        <div className="flex flex-row items-center gap-2">
          {/* Se tem nova estrutura de preços, mostrar tags baseadas nela */}
          {prices?.sale && (
            <div 
              className="flex flex-col items-start justify-center px-2 py-0.5 rounded-full"
              style={{ backgroundColor: "#6386AC" }}
            >
              <span className="text-xs leading-[1.5] text-neutral-white-soft">Venda</span>
            </div>
          )}
          {prices?.rental && (
            <div 
              className="flex flex-col items-start justify-center px-2 py-0.5 rounded-full"
              style={{ backgroundColor: "#6C757D" }}
            >
              <span className="text-xs leading-[1.5] text-neutral-white-soft">Locação</span>
            </div>
          )}
          
          {/* Fallback para compatibilidade */}
          {!prices && saleType && (
            <div 
              className="flex flex-col items-start justify-center px-2 py-0.5 rounded-full"
              style={{ backgroundColor: saleType === "Locação" ? "#6C757D" : "#6386AC" }}
            >
              <span className="text-xs leading-[1.5] text-neutral-white-soft">{saleType}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Botão de Favorito */}
          <div onClick={(e) => e.stopPropagation()}>
            <FavoriteButton
              propertyId={id}
              propertyData={{
                title,
                image: images[0],
                price,
                address,
              }}
              variant="icon-only"
              size="sm"
              showToast={false}
            />
          </div>
          {/* Botão de Compartilhar */}
          <button
            className="p-1 hover:bg-neutral-gray-bg rounded-full transition-colors"
            onClick={handleShare}
          >
            <Share2 className="w-6 h-6 text-neutral-gray-light" />
          </button>
        </div>
      </div>

      {/* Container/Imagem e Rótulos */}
      <div className="flex flex-col gap-3 items-start w-full">
        {/* Image Container with Overlays */}
        <div className="relative group" style={{ width: styles.isFlexible ? '100%' : `${styles.imageWidth}px`, height: `${styles.imageHeight}px` }}>
          <img
            src={images[currentImageIndex]}
            alt={title}
            className="w-full h-full object-cover rounded-3xl"
          />

          {/* Selo - Anúncio novo */}
          {isNew && (
            <div className="absolute top-4 left-4 backdrop-blur-sm bg-[rgba(255,255,255,0.4)] flex gap-1 items-center px-2 py-1 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm leading-[1.5] text-neutral-dark">Anúncio novo</span>
            </div>
          )}

          {/* Partner Badge */}
          {badges.map((badge, index) => (
            badge.type === "partner" && (
              <div key={index} className="absolute top-4 left-4 backdrop-blur-sm bg-[rgba(255,255,255,0.4)] flex gap-1 items-center px-2 py-1 rounded-full">
                <div className="flex items-center">
                  <span className="text-xs font-semibold text-neutral-dark">EasyLiving</span>
                  <span className="text-[8px] text-neutral-dark">Imobi</span>
                </div>
              </div>
            )
          ))}

          {/* Badge/Engajamento */}
          {engagementCount && (
            <div className="absolute top-4 right-4 backdrop-blur-sm bg-[rgba(0,0,0,0.25)] flex gap-1 items-center px-2 py-1 rounded-full">
              <Flame className="w-5 h-5 text-brand-yellow" />
              <span className="text-sm font-medium leading-[1.5] text-brand-yellow">
                {engagementCount}
              </span>
            </div>
          )}

          {/* Indicadores de carrossel */}
          {images.length > 1 && (
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex items-center gap-1">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  className={cn(
                    "w-1 h-1 rounded-full transition-colors",
                    currentImageIndex === index ? "bg-white" : "bg-white/50"
                  )}
                />
              ))}
            </div>
          )}
        </div>

      {/* Container/Texto */}
      <div className={cn("flex flex-col items-start w-full", styles.bodyGapClass)}>
          {/* Título/Imóvel */}
          <div className="flex flex-col gap-1 items-start w-full">
          <h3 className={cn("font-medium leading-normal text-neutral-dark min-w-full", styles.titleSize)}>
              {title}
            </h3>
            {(type || code) && (
            <p className={cn("leading-[1.5] text-brand-gold", styles.metaSize)}>
                {[type, code && `Código #${code}`].filter(Boolean).join(' • ')}
              </p>
            )}
            {attributes && (
            <p className={cn("font-normal leading-[1.3] text-neutral-gray-dark whitespace-nowrap", styles.attributesSize)}>
                {attributes}
              </p>
            )}
          </div>

          {/* Preço/Valor - Nova lógica */}
        <div className="flex flex-col items-start w-full gap-3">
          {/* Se tem AMBAS as finalidades */}
          {prices?.sale && prices?.rental ? (
            <>
              {/* Seção de Venda */}
              <div className="flex flex-col items-start w-full">
                <span className="text-[10px] font-medium text-neutral-gray-dark uppercase">
                  {prices.sale.label}
                </span>
                <span className={cn("font-bold text-neutral-black", styles.priceSize)}>
                  {prices.sale.value}
                </span>
                {(prices.sale.condominium || prices.sale.iptu) && (
                  <span className={cn("font-normal text-neutral-gray-dark", styles.addCostsSize)}>
                    {[
                      prices.sale.condominium && `Cond. ${prices.sale.condominium}`,
                      prices.sale.iptu && `IPTU ${prices.sale.iptu}`
                    ].filter(Boolean).join(' + ')}
                  </span>
                )}
              </div>
              
              {/* Divisor */}
              <div className="w-full h-px bg-neutral-gray-bg"></div>
              
              {/* Seção de Locação */}
              <div className="flex flex-col items-start w-full">
                <span className="text-[10px] font-medium text-neutral-gray-dark uppercase">
                  {prices.rental.label}
                </span>
                <div className="flex gap-1 items-baseline">
                  <span className={cn("font-bold text-neutral-black", styles.priceSize)}>
                    {prices.rental.value}
                  </span>
                  <span className={cn("font-normal text-neutral-black", styles.priceUnitSize)}>
                    {prices.rental.unit}
                  </span>
                </div>
                {(prices.rental.condominium || prices.rental.iptu) && (
                  <span className={cn("font-normal text-neutral-gray-dark", styles.addCostsSize)}>
                    {[
                      prices.rental.condominium && `Cond. ${prices.rental.condominium}`,
                      prices.rental.iptu && `IPTU ${prices.rental.iptu}`
                    ].filter(Boolean).join(' + ')}
                  </span>
                )}
              </div>
            </>
          ) : prices?.sale ? (
            /* Apenas Venda */
            <div className="flex flex-col items-start">
              <span className={cn("font-bold", styles.priceSize)}>{prices.sale.value}</span>
              {(prices.sale.condominium || prices.sale.iptu) && (
                <span className={cn("font-normal text-neutral-gray-dark", styles.addCostsSize)}>
                  {[
                    prices.sale.condominium && `Cond. ${prices.sale.condominium}`,
                    prices.sale.iptu && `IPTU ${prices.sale.iptu}`
                  ].filter(Boolean).join(' + ')}
                </span>
              )}
            </div>
          ) : prices?.rental ? (
            /* Apenas Locação */
            <div className="flex flex-col items-start">
              <div className="flex gap-1 items-center">
                <span className={cn("font-bold", styles.priceSize)}>{prices.rental.value}</span>
                <span className={cn("font-normal", styles.priceUnitSize)}>{prices.rental.unit}</span>
              </div>
              {(prices.rental.condominium || prices.rental.iptu) && (
                <span className={cn("font-normal", styles.addCostsSize)}>
                  {[
                    prices.rental.condominium && `Cond. ${prices.rental.condominium}`,
                    prices.rental.iptu && `IPTU ${prices.rental.iptu}`
                  ].filter(Boolean).join(' + ')}
                </span>
              )}
            </div>
          ) : (
            /* Fallback (compatibilidade) */
            <div className="flex flex-col items-start">
              <div className="flex gap-1 items-center">
                <span className={cn("font-bold", styles.priceSize)}>{price}</span>
                {priceUnit && <span className={cn("font-normal", styles.priceUnitSize)}>{priceUnit}</span>}
              </div>
              {additionalCosts && (
                <span className={cn("font-normal", styles.addCostsSize)}>{additionalCosts}</span>
              )}
            </div>
          )}
        </div>

          {/* Endereço */}
        <div className="flex gap-1 items-center w-full">
          <MapPin className={cn("text-neutral-gray-dark", styles.iconSize)} />
          <p className={cn("font-normal leading-[1.3] text-neutral-gray-dark flex-1 min-w-0", styles.addressSize)}>
              {address}
            </p>
          </div>
        </div>
      </div>

      {/* Toast de confirmação de cópia */}
      {showCopyToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-neutral-black text-white px-6 py-3 rounded-full shadow-lg animate-in fade-in slide-in-from-bottom-4">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Link copiado!</span>
          </div>
        </div>
      )}
    </article>
  );
}

export { type PropertyCardProps, type PropertyBadge };
export default PropertyCard;
